import { useState } from 'react'
import { Shield, Search, RefreshCw, ExternalLink, CheckCircle2, AlertTriangle, XCircle, FileText } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type Tab = 'cosign' | 'slsa' | 'intoto'

const TABS: { id: Tab; label: string; help: string }[] = [
  { id: 'cosign', label: 'Cosign verify',       help: 'Is this artifact signed in Sigstore Rekor?' },
  { id: 'slsa',   label: 'SLSA grade',          help: 'Score the build provenance L0-L4.' },
  { id: 'intoto', label: 'In-toto attestations', help: 'Decoded attestation chain with predicate type + materials.' },
]

export default function SupplyChainTrustPage() {
  const [tab, setTab] = useState<Tab>('cosign')
  const [hash, setHash] = useState('')
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lookup = async () => {
    if (!hash.trim()) return
    setLoading(true); setError(null); setData(null)
    const endpoint = {
      cosign: '/api/sec/sigstore-verify',
      slsa:   '/api/sec/slsa-grade',
      intoto: '/api/sec/intoto-attestations',
    }[tab]
    try {
      const r = await api.get(endpoint, { params: { artifact_sha256: hash.trim() } })
      setData(r.data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Lookup failed')
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Shield size={24} className="text-emerald" /> Supply-Chain Trust
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Sigstore Rekor transparency log. Verify signatures, grade SLSA provenance, browse attestations — free public log.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map(t => (
          <button key={t.id} type="button" onClick={() => { setTab(t.id); setData(null); setError(null) }}
            className={clsx('px-3 py-2 text-xs font-medium transition-colors',
              tab === t.id
                ? 'text-text-primary border-b-2 border-blue -mb-px'
                : 'text-text-muted hover:text-text-secondary')}>
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-text-muted -mt-3">{TABS.find(t => t.id === tab)?.help}</p>

      {/* Hash input */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-2">
          <label className="space-y-1 flex-1 min-w-[260px]">
            <span className="text-[10px] text-text-muted uppercase tracking-wider">Artifact sha256</span>
            <input value={hash} onChange={e => setHash(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && lookup()}
              placeholder="sha256:abc123… (or just abc123…)"
              className="input w-full py-1.5 text-xs font-mono" />
          </label>
          <button type="button" onClick={lookup} disabled={loading || !hash.trim()}
            className="btn-primary text-xs">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
            Look up
          </button>
        </div>
        <p className="text-[10px] text-text-muted mt-2">
          Get an artifact's sha256 with <code className="font-mono">cosign download attestation &lt;ref&gt; | sha256sum</code>
          {' '}or from a Docker image's manifest. Free Rekor public log — no auth.
        </p>
      </div>

      {error && <div className="card p-3 bg-rose/10 border border-rose/30 text-xs text-rose-light">{error}</div>}

      {/* Results per tab */}
      {data && tab === 'cosign' && <CosignResult data={data} />}
      {data && tab === 'slsa'   && <SlsaResult data={data} />}
      {data && tab === 'intoto' && <IntotoResult data={data} />}
    </div>
  )
}

function CosignResult({ data }: { data: any }) {
  const tone = data.tone || 'blue'
  const Icon = data.verdict === 'signed' ? CheckCircle2 : data.verdict === 'unsigned' ? AlertTriangle : XCircle
  const TONE: Record<string, string> = {
    emerald: 'bg-emerald/15 ring-emerald/40 text-emerald',
    amber:   'bg-amber/15 ring-amber/40 text-amber-light',
    rose:    'bg-rose/15 ring-rose/40 text-rose',
    blue:    'bg-blue/15 ring-blue/40 text-blue-light',
  }
  return (
    <div className="space-y-3">
      <div className={clsx('rounded-xl p-5 ring-2 flex items-center gap-3', TONE[tone])}>
        <Icon size={28} />
        <div>
          <div className="text-xs uppercase tracking-widest opacity-80">Verdict</div>
          <div className="text-2xl font-black uppercase">{data.verdict}</div>
          <div className="text-[10px] mt-1 opacity-80">{data.count} Rekor entries · hash {data.hash?.slice(0, 24)}…</div>
        </div>
      </div>
      {data.entries?.length > 0 && (
        <div className="card p-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
                <th className="text-left py-2 px-2">Kind</th>
                <th className="text-left py-2 px-2">UUID</th>
                <th className="text-left py-2 px-2">Integrated</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e: any) => (
                <tr key={e.uuid} className="border-b border-border/50 hover:bg-surface-hover">
                  <td className="py-1.5 px-2 font-mono text-blue-light">{e.kind}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted truncate max-w-[260px]" title={e.uuid}>{e.uuid}</td>
                  <td className="py-1.5 px-2 text-[10px] text-text-muted">
                    {e.integrated_time ? new Date(e.integrated_time * 1000).toISOString().slice(0, 19) : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <a href={e.view_url} target="_blank" rel="noreferrer" className="text-blue-light hover:underline inline-flex items-center gap-1 text-[10px]">
                      view <ExternalLink size={9} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SlsaResult({ data }: { data: any }) {
  const g = data.grade || {}
  const TONE: Record<string, string> = {
    emerald: 'bg-emerald/15 ring-emerald/40 text-emerald',
    blue:    'bg-blue/15 ring-blue/40 text-blue-light',
    amber:   'bg-amber/15 ring-amber/40 text-amber-light',
    rose:    'bg-rose/15 ring-rose/40 text-rose',
  }
  return (
    <div className="space-y-3">
      <div className={clsx('rounded-xl p-5 ring-2', TONE[g.tone || 'blue'])}>
        <div className="text-xs uppercase tracking-widest opacity-80">SLSA grade</div>
        <div className="text-3xl font-black mt-1">{g.label || '—'}</div>
        <div className="text-[10px] mt-1 opacity-80">
          {data.rekor_entry_count} Rekor entries · attestation {data.chosen_entry_uuid?.slice(0, 16) || '—'}
        </div>
      </div>
      <div className="card p-4">
        <h3 className="text-xs font-semibold text-text-primary mb-2">Reasoning</h3>
        <ul className="space-y-1 text-[11px] text-text-secondary">
          {(g.reasons || []).map((r: string, i: number) => <li key={i}>· {r}</li>)}
        </ul>
        {g.builder_id && (
          <p className="text-[10px] text-text-muted mt-2">
            Builder: <code className="font-mono">{g.builder_id}</code>
            {g.materials_count > 0 && <> · {g.materials_count} materials recorded</>}
          </p>
        )}
      </div>
    </div>
  )
}

function IntotoResult({ data }: { data: any }) {
  if (data.error) return <div className="card p-3 text-xs text-rose-light">Error: {data.error}</div>
  if (!data.attestations?.length) {
    return <div className="card p-6 text-center text-text-muted text-xs italic">No in-toto attestations found for this artifact.</div>
  }
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-text-muted">{data.attestations.length} attestation(s) found in Rekor.</p>
      {data.attestations.map((a: any) => (
        <div key={a.uuid} className="card p-3 border border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText size={12} className="text-blue-light" />
              <span className="text-xs font-semibold text-text-primary">{a.predicate_type || a.kind}</span>
            </div>
            <a href={a.view_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1">
              raw <ExternalLink size={9} />
            </a>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="text-[9px] uppercase text-text-muted">UUID</div>
              <div className="font-mono text-[10px] text-text-secondary truncate" title={a.uuid}>{a.uuid}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase text-text-muted">Integrated</div>
              <div className="font-mono text-[10px] text-text-secondary">
                {a.integrated_time ? new Date(a.integrated_time * 1000).toISOString().slice(0, 19) : '—'}
              </div>
            </div>
          </div>
          {a.subject?.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] uppercase text-text-muted mb-1">Subjects ({a.subject.length})</div>
              <ul className="text-[10px] font-mono text-text-secondary space-y-0.5">
                {a.subject.slice(0, 3).map((s: any, i: number) => (
                  <li key={i} className="truncate">{s.name} · {Object.entries(s.digest || {}).map(([k, v]) => `${k}:${(v as string).slice(0, 12)}…`).join(', ')}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
